import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  BillingPricingMode,
  OrganizationBillingCategoryCode,
} from "@api/shared-runtime/enums";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MONEY_STEP,
  PERCENT_STEP,
  WHOLE_QUANTITY_STEP,
  FREE_CATEGORY_LABEL,
  DOCUMENT_SIGNATURE_COST,
  describeCategoryStatus,
  describeIncludedQuantity,
  formatPlanCurrency,
  getIncludedQuantityLabel,
  type BillingPlanCategory,
  type BillingPlanFormValues,
} from "./billing-plan-values";
import {
  WAGE_PASS_THROUGH_OFF_HELP,
  WAGE_PASS_THROUGH_ON_HELP,
  WAGE_PASS_THROUGH_SWITCH_LABEL,
} from "./wage-pass-through-copy";

const PRICING_MODE_LABELS: Record<BillingPricingMode, string> = {
  [BillingPricingMode.PerUnit]: "Flat amount per approved shift",
  [BillingPricingMode.PercentOfBasis]: "Percentage of wages",
};

export function BillingPlanCategoryFields({
  category,
  index,
  onChange,
}: {
  category: BillingPlanCategory;
  index: number;
  onChange: () => void;
}) {
  const { register, control } = useFormContext<BillingPlanFormValues>();
  const value = useWatch({ control, name: `prices.${index}` });
  const fieldId = `plan-${category.categoryCode}`;
  const hasPerUnitRate = value.unitAmount.trim() !== "";
  const rateLabel = `Fee per ${category.unitNoun} (USD)`;
  const showDocumentCost =
    category.categoryCode === OrganizationBillingCategoryCode.DocumentSignature;
  const showWagePassThrough =
    category.categoryCode === OrganizationBillingCategoryCode.ShiftMarkup;
  const wagePassThroughEnabled = useWatch({
    control,
    name: "wagePassThroughEnabled",
  });
  const status = describeCategoryStatus({ category, price: value });
  return (
    <section
      aria-labelledby={fieldId}
      className="space-y-3 rounded-lg border p-4"
    >
      <div className="flex items-center justify-between gap-2">
        <h4 id={fieldId} className="text-sm font-medium">
          {category.displayName}
        </h4>
        <span className="text-sm text-muted-foreground">{status}</span>
      </div>
      {category.supported && (
        <div className="space-y-3">
          {category.pricingModes.length > 1 && (
            <div className="space-y-1">
              <Label htmlFor={`${fieldId}-mode`}>Shift markup pricing</Label>
              <Controller
                control={control}
                name={`prices.${index}.pricingMode`}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(mode) => {
                      field.onChange(mode);
                      onChange();
                    }}
                  >
                    <SelectTrigger id={`${fieldId}-mode`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {category.pricingModes.map((mode) => (
                        <SelectItem key={mode} value={mode}>
                          {PRICING_MODE_LABELS[mode]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          )}
          {value.pricingMode === BillingPricingMode.PercentOfBasis ? (
            <div className="space-y-1">
              <Label htmlFor={`${fieldId}-percent`}>
                Percentage of wages (%)
              </Label>
              <Input
                id={`${fieldId}-percent`}
                type="number"
                min="0"
                step={PERCENT_STEP}
                placeholder={FREE_CATEGORY_LABEL}
                {...register(`prices.${index}.basisPercent`)}
              />
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor={`${fieldId}-rate`}>{rateLabel}</Label>
                <Input
                  id={`${fieldId}-rate`}
                  type="number"
                  min="0"
                  step={MONEY_STEP}
                  placeholder={FREE_CATEGORY_LABEL}
                  aria-describedby={
                    showDocumentCost ? `${fieldId}-cost` : undefined
                  }
                  {...register(`prices.${index}.unitAmount`)}
                />
                {showDocumentCost && (
                  <p
                    id={`${fieldId}-cost`}
                    className="text-xs text-muted-foreground"
                  >
                    Cost: {formatPlanCurrency(DOCUMENT_SIGNATURE_COST)} per
                    signed document.
                  </p>
                )}
              </div>
              {hasPerUnitRate && (
                <div className="space-y-1">
                  <Label htmlFor={`${fieldId}-included`}>
                    {getIncludedQuantityLabel(category.unitNoun)}
                  </Label>
                  <Input
                    id={`${fieldId}-included`}
                    type="number"
                    min="0"
                    step={WHOLE_QUANTITY_STEP}
                    required
                    aria-describedby={`${fieldId}-included-help`}
                    {...register(`prices.${index}.includedQuantity`)}
                  />
                  <p
                    id={`${fieldId}-included-help`}
                    className="text-xs text-muted-foreground"
                  >
                    {describeIncludedQuantity({
                      includedQuantity: value.includedQuantity,
                      unitAmount: value.unitAmount,
                      unitNoun: category.unitNoun,
                    })}
                  </p>
                </div>
              )}
            </div>
          )}
          {showWagePassThrough && (
            <div className="flex items-center justify-between gap-4 border-t pt-3">
              <div className="space-y-0.5">
                <Label
                  htmlFor={`${fieldId}-wage-pass-through`}
                  className="font-medium"
                >
                  {WAGE_PASS_THROUGH_SWITCH_LABEL}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {wagePassThroughEnabled
                    ? WAGE_PASS_THROUGH_ON_HELP
                    : WAGE_PASS_THROUGH_OFF_HELP}
                </p>
              </div>
              <Controller
                control={control}
                name="wagePassThroughEnabled"
                render={({ field }) => (
                  <Switch
                    id={`${fieldId}-wage-pass-through`}
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      onChange();
                    }}
                  />
                )}
              />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
